import { useCallback, useState } from 'react';
import { persistInstructionUpdates } from './persistInstructionUpdates';
import { requestInstructionRevisions } from './instruction-assistant-utils';
import { isAgenticLearnUser } from '../../utils/agenticLearn';

const IDLE_STATE = {
  status: 'idle',
  kind: 'template',
  noteSnapshot: [],
  oldInstructions: {},
  newInstructions: {},
  oldExamples: {},
  newExamples: {},
  showInstructionModal: false,
};

/**
 * Owns the Instruction Assistant's apply + undo lifecycle for a host view: the
 * updating/updated status strip and the section-revert undo modal. The host
 * supplies an instructions adapter, and — for the EMR note path — a note adapter
 * that can snapshot, re-comprehend, and revert the note.
 *
 * Params:
 *   apiClient, configId                 — for the generate + persist calls.
 *   conversationId                      — optional visit ID recorded with changes.
 *   instructions.getCurrent()           — () => { [key]: instruction } (snapshot old text).
 *   instructions.applyLocal(updates)    — merge a { [key]: instruction } map into host state.
 *   instructions.getSectionMetadata()   — optional () => metadata[] sent with generate.
 *   examples.getCurrent()                — optional () => { [key]: examples }.
 *   examples.applyLocal(updates)         — optional merge into host example state.
 *   note                                — optional { snapshot(keys), reComprehend(keys), revert(answers) }
 *                                         enabling the "apply to template and note" path.
 *   onError(message)                    — optional, surfaced on undo/revert failures.
 *
 * Returns { update, applyTemplate, applyTemplateAndNote, openUndo, resolveUndo }.
 * applyTemplate / applyTemplateAndNote take the kept changes [{directive, sections}]
 * and optional feedback context from the modal, and are the modal's
 * onSaveToTemplate / onSaveToTemplateAndNote handlers.
 *
 * Agentic Learn users (features.signed_note_learner === 'agentic_learn'):
 * applyTemplate hands the kept changes to the agent instead, silently (no status
 * strip: there is no saved change to undo, the agent works in the background),
 * falling back to the normal save when it is not accepted; applyTemplateAndNote
 * is null so the modal hides the note option.
 */
export function useInstructionAssistantUpdate({
  apiClient,
  configId,
  conversationId = null,
  instructions: {
    getCurrent: getCurrentInstructions,
    applyLocal,
    getSectionMetadata = null,
  },
  examples: {
    getCurrent: getCurrentExamples = () => ({}),
    applyLocal: applyLocalExamples = () => {},
  } = {},
  note = null,
  onError = null,
}) {
  const [state, setState] = useState(IDLE_STATE);

  const close = () => setState(IDLE_STATE);
  const reset = useCallback(() => setState(IDLE_STATE), []);

  // generate revisions -> snapshot old (and, for the note path, the EMR answers)
  // -> persist -> merge locally -> re-comprehend. Drives the status strip and, on
  // failure, resets it and rethrows so the modal can surface the error.
  const apply = async (keptChanges, kind, feedbackContext = {}) => {
    setState({ ...IDLE_STATE, kind, status: 'updating' });
    try {
      const sectionMetadata = getSectionMetadata ? getSectionMetadata() : null;
      const instructionChangeEvent = {
        eventId: crypto.randomUUID(),
        conversationId,
        appliedToNote: kind === 'note',
      };
      const { newInstructions, newExamples } = await requestInstructionRevisions(
        apiClient,
        configId,
        keptChanges,
        sectionMetadata,
        feedbackContext,
        instructionChangeEvent,
      );
      const changedKeys = [
        ...new Set([
          ...Object.keys(newInstructions),
          ...Object.keys(newExamples),
        ]),
      ];
      const current = getCurrentInstructions() || {};
      const currentExamples = getCurrentExamples() || {};
      const oldInstructions = {};
      for (const key of Object.keys(newInstructions)) {
        if (current[key] !== undefined) {
          oldInstructions[key] = current[key];
        }
      }
      const oldExamples = {};
      for (const key of Object.keys(newExamples)) {
        oldExamples[key] = Array.isArray(currentExamples[key])
          ? [...currentExamples[key]]
          : [];
      }
      let noteSnapshot = [];
      if (kind === 'note' && note && note.snapshot) {
        try {
          noteSnapshot = await note.snapshot(changedKeys);
        } catch (err) {
          console.error('Could not snapshot EMR note before update:', err);
        }
      }
      const persistedExamples = await persistInstructionUpdates(
        apiClient,
        configId,
        newInstructions,
        newExamples,
      );
      if (Object.keys(newInstructions).length > 0) {
        applyLocal(newInstructions);
      }
      if (Object.keys(persistedExamples).length > 0) {
        applyLocalExamples(persistedExamples);
      }
      if (kind === 'note' && note && note.reComprehend) {
        await note.reComprehend(changedKeys);
      }
      setState({
        status: 'updated',
        kind,
        noteSnapshot,
        oldInstructions,
        newInstructions,
        oldExamples,
        newExamples: persistedExamples,
        showInstructionModal: false,
      });
    } catch (err) {
      close();
      throw err;
    }
  };

  // Hand the kept changes to Agentic Learn; anything it does not accept is
  // saved the usual way.
  const applyTemplateAgentic = async (keptChanges, feedbackContext) => {
    const feedback = feedbackContext || {};
    const triggerConfigId = configId;
    let response = null;
    try {
      response = await apiClient.triggerAgenticLearn(
        triggerConfigId,
        conversationId,
        'instruction_assistant',
        {
          request: feedback.transcript || '',
          source: feedback.source === 'dictation' ? 'dictated' : 'typed',
          changes: keptChanges,
          sectionMetadata: getSectionMetadata ? getSectionMetadata() : null,
        },
      );
    } catch (err) {
      response = null;
    }
    if (!response?.accepted) {
      return apply(keptChanges, 'template', feedbackContext);
    }
    return undefined;
  };

  const applyTemplate = (keptChanges, feedbackContext) =>
    isAgenticLearnUser(apiClient)
      ? applyTemplateAgentic(keptChanges, feedbackContext)
      : apply(keptChanges, 'template', feedbackContext);
  const applyTemplateAndNote = (keptChanges, feedbackContext) =>
    apply(keptChanges, 'note', feedbackContext);

  // Undo opens the section picker; the restore happens after the user chooses.
  const openUndo = () =>
    setState((prev) => ({
      ...prev,
      status: 'idle',
      showInstructionModal: true,
    }));

  // Revert the prior note text (note path) and saved preferences for the chosen
  // sections. Empty list = keep everything.
  const resolveUndo = async (sectionsToRevert) => {
    const { kind, noteSnapshot, oldInstructions, oldExamples } = state;
    const selected = sectionsToRevert || [];
    close();

    if (kind === 'note' && note && note.revert && selected.length > 0) {
      const answers = (noteSnapshot || [])
        .filter((snap) => selected.includes(snap.Key))
        .map(({ Question, Answer }) => ({ Question, Answer }));
      if (answers.length > 0) {
        try {
          await note.revert(answers);
        } catch (err) {
          console.error('Could not revert note:', err);
          if (onError) {
            onError('Could not revert the note. Please try again.');
          }
        }
      }
    }

    const revert = {};
    selected.forEach((key) => {
      if (oldInstructions[key] !== undefined) {
        revert[key] = oldInstructions[key];
      }
    });
    const revertExamples = {};
    selected.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(oldExamples, key)) {
        revertExamples[key] = oldExamples[key];
      }
    });
    if (
      Object.keys(revert).length > 0 ||
      Object.keys(revertExamples).length > 0
    ) {
      try {
        const persistedExamples = await persistInstructionUpdates(
          apiClient,
          configId,
          revert,
          revertExamples,
        );
        if (Object.keys(revert).length > 0) {
          applyLocal(revert);
        }
        if (Object.keys(persistedExamples).length > 0) {
          applyLocalExamples(persistedExamples);
        }
      } catch (err) {
        console.error('Could not revert preferences:', err);
        if (onError) {
          onError('Could not revert the preferences. Please try again.');
        }
      }
    }
  };

  return {
    state,
    reset,
    applyTemplate,
    applyTemplateAndNote: isAgenticLearnUser(apiClient)
      ? null
      : applyTemplateAndNote,
    openUndo,
    resolveUndo,
  };
}
